import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { MoreVertical } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../../../common/components/Button";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import PageHeader from "../../../common/components/PageHeader";
import { toast } from "../../../common/components/Toast";
import { UserAvatar } from "../../../common/components/UserAvatar";
import { SPACING } from "../../../common/constants/spacing";
import TemplateActionsMenu from "../../components/TemplateActionsMenu";
import { useReportTemplates } from "../../services/reportTemplates";

const ReportTemplates = () => {
  const { t } = useTranslation("reports");
  const navigate = useNavigate();
  const { templates, loading, deleteTemplate, deleting } = useReportTemplates();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleMenuOpen = (event, template) => {
    setAnchorEl(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    if (selectedTemplate) {
      navigate(`/report-templates/${selectedTemplate.id}/edit`);
      setSelectedTemplate(null);
      handleMenuClose();
    }
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTemplate) return;

    try {
      await deleteTemplate(selectedTemplate.id);
      toast.success(t("templates.messages.deleteSuccess"));
      handleDeleteModalClose();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error(t("templates.messages.deleteError"));
    }
  };

  if (loading) {
    return <div data-testid="templates-loading">{t("templates.loading")}</div>;
  }

  return (
    <>
      <PageHeader
        title={t("templates.title")}
        description={t("templates.description")}
      />

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/report-templates/create")}
          data-testid="create-template-button"
        >
          {t("templates.createNew")}
        </Button>
      </Box>

      <TableContainer
        sx={{ mt: SPACING.sm }}
        data-testid="templates-table-container"
      >
        <Table
          sx={{ minWidth: 650 }}
          aria-label="report templates table"
          data-testid="templates-table"
        >
          <TableHead>
            <TableRow>
              <TableCell>{t("templates.table.title")}</TableCell>
              <TableCell>{t("templates.table.createdBy")}</TableCell>
              <TableCell>{t("templates.table.createdAt")}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-testid="templates-table-body">
            {templates.map((template) => (
              <TableRow
                key={template.id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                data-testid={`template-row-${template.id}`}
              >
                <TableCell component="th" scope="row">
                  {template.title || t("templates.untitled")}
                </TableCell>
                <TableCell>
                  <UserAvatar
                    name={template?.created_by?.email || t("table.unknownUser")}
                    image={template?.created_by?.avatar}
                    size={32}
                    tooltip={template?.created_by?.email}
                  />
                </TableCell>
                <TableCell>
                  {formatDistanceToNow(new Date(template.created_at), {
                    addSuffix: true,
                  })}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, template)}
                    aria-label="more options"
                    data-testid={`template-actions-button-${template.id}`}
                  >
                    <MoreVertical size={18} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <TemplateActionsMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onView={handleView}
        onDelete={handleDeleteClick}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        title={t("templates.deleteModal.title")}
        message={t("templates.deleteModal.message")}
        confirmText={t("templates.deleteModal.confirm")}
        cancelText={t("templates.deleteModal.cancel")}
        confirmColor="error"
        loading={deleting}
      />
    </>
  );
};

export default ReportTemplates;
